import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { curatorApi } from '@/api/users';
import { humanizeError } from '@/lib/errors';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MEmpty, MIcon, MRow, MSection, MTag, MTitle } from '@/components/mobile/MobileKit';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Pill } from '@/components/ui/Pill';
import { lessonRowAction, parseCuratorGrade } from '@/lib/lessonFeedback';
import { MANUAL_NOTE_KEYS, manualCompletionNote, parseNextOpened, stuckLessonIds } from '@/lib/lessonManualActions';
import { LessonGradeField } from './LessonGradeField';
import { LessonFeedbackEditor } from './LessonFeedbackEditor';
import { OpenNextLessonButton } from './OpenNextLessonButton';

/**
 * Уроки ученика глазами куратора и админа: что пройдено, а что нет, и вход в
 * разбор ответов по каждому уроку.
 *
 * ⚠️ ОДНА СТРАНИЦА НА ДВА РАЗДЕЛА. Она подключена и в `/curator`, и в
 * `/admin` — как `CuratorTasksPage`. Адрес возврата и адреса уроков строятся
 * от текущего раздела, иначе админ, провалившись в урок, вышел бы в
 * кураторскую и упёрся в проверку роли.
 *
 * Права проверяет сервер: ручка `/progress/students/:id/lessons` отдаёт данные
 * куратору только по его ученикам, админу — по любому. Здесь никакой проверки
 * нет и быть не должно — фронт не место для правил доступа.
 */
export function StudentProgressPage() {
  const { t, i18n } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  const isMobile = useIsMobile();

  /** Раздел, из которого пришли: от него строятся все ссылки. */
  const base = pathname.startsWith('/admin') ? '/admin' : '/curator';

  const lessons = useQuery({
    queryKey: ['staff', 'student-lessons', userId],
    queryFn: () => progressApi.getStudentLessons(userId!),
    enabled: !!userId,
  });

  // Имя ученика для шапки. Ручка кураторская, админ по ней тоже проходит
  // (RequireRole("curator","admin") в users-service) — отдельной ветки не надо.
  const student = useQuery({
    queryKey: ['staff', 'student-card', userId],
    queryFn: () => curatorApi.getStudent(userId!),
    enabled: !!userId,
  });

  const data = lessons.data;

  // Поиск по названию урока — локальный: список уроков одного ученика
  // помещается в память целиком, ходить за этим на сервер незачем.
  const marathons = useMemo(() => {
    const all = data?.marathons ?? [];
    const q = query.trim().toLocaleLowerCase();
    if (!q) return all;
    return all
      .map((m) => ({ ...m, lessons: m.lessons.filter((l) => l.title.toLocaleLowerCase().includes(q)) }))
      .filter((m) => m.lessons.length > 0);
  }, [data, query]);

  const back = (
    <Link
      to={`${base}/students/${userId}`}
      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
    >
      <span className="material-symbols-outlined text-[18px]">arrow_back</span>
      {t('studentProgress.back')}
    </Link>
  );

  const name = student.data?.full_name || t('curatorStudents.studentFallback');
  const total = data?.total ?? 0;
  const passed = data?.passed ?? 0;
  const percent = total > 0 ? Math.round((passed / total) * 100) : 0;

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.1) ──────────────────────────
  // Строка урока целиком ведёт в разбор: отдельная кнопка «Открыть урок» на 390 съедала название до ~10 знаков.
  // Действия — те же компоненты, что на компьютере, под строкой урока: правила режима курса живут в них, а не здесь.
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          back={`${base}/students/${userId}`}
          title={name}
          subtitle={lessons.isSuccess ? `${t('studentProgress.passedOf', { passed, total })} · ${percent}%` : undefined}
        />
        {lessons.isSuccess && total > 0 && (
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-high">
            <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
          </div>
        )}

        {lessons.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : lessons.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(lessons.error)}</p>
        ) : total === 0 ? (
          <MEmpty icon="menu_book" title={t('studentProgress.emptyTitle')} hint={t('studentProgress.emptyDesc')} />
        ) : (
          <>
            <label className="flex items-center gap-2 rounded-full bg-surface-container-low px-4">
              <span className="material-symbols-outlined text-[18px] text-on-surface-variant">search</span>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('studentProgress.searchPlaceholder')}
                className="h-11 w-full bg-transparent text-[14px] text-on-surface outline-none placeholder:text-on-surface-variant"
              />
            </label>
            {marathons.length === 0 ? (
              <p className="px-0.5 text-[13px] text-on-surface-variant">{t('studentProgress.nothingFound')}</p>
            ) : (
              marathons.map((m) => {
                const stuck = stuckLessonIds(
                  data?.marathons.find((x) => x.marathon_id === m.marathon_id)?.lessons ?? m.lessons,
                );
                return (
                  <div key={m.marathon_id} className="space-y-2">
                    <MSection
                      title={m.title}
                      right={<MTag tone={m.passed > 0 ? 'info' : 'muted'}>{t('studentProgress.passedOf', { passed: m.passed, total: m.total })}</MTag>}
                    />
                    <MBlock>
                      {m.lessons.map((l, i) => {
                        const nextOpened = parseNextOpened(l.next_opened);
                        const isStuck = !l.is_completed && stuck.has(l.lesson_id);
                        const manualNote = manualCompletionNote(l);
                        const notes = [
                          l.completed_manually ? t('studentProgress.completedManually') : '',
                          manualNote ? t(MANUAL_NOTE_KEYS[manualNote]) : '',
                          nextOpened
                            ? t('studentProgress.nextOpenedBy', {
                                name: nextOpened.name ?? t('studentProgress.unknownCurator'),
                                date: new Date(nextOpened.at).toLocaleString(i18n.language),
                              })
                            : '',
                        ].filter(Boolean);
                        const act = userId ? lessonRowAction(m.grading_mode, l, isStuck) : null;
                        const action =
                          act === 'feedback' ? (
                            <LessonFeedbackEditor
                              studentId={userId!}
                              lessonId={l.lesson_id}
                              lessonTitle={l.title}
                              curatorGrade={parseCuratorGrade(l.curator_grade)}
                              isCompleted={l.is_completed}
                              canComplete={isStuck}
                            />
                          ) : act === 'grade' ? (
                            <LessonGradeField
                              studentId={userId!}
                              lessonId={l.lesson_id}
                              grade={l.grade}
                              isCompleted={l.is_completed}
                            />
                          ) : act === 'openNext' ? (
                            <OpenNextLessonButton studentId={userId!} lessonId={l.lesson_id} lessonTitle={l.title} />
                          ) : null;
                        return (
                          <MRow
                            key={l.lesson_id}
                            wrap
                            leading={
                              // Оба знака обязаны быть в вырезанном шрифте значков — те же, что на компьютере.
                              <MIcon icon={l.is_completed ? 'check' : 'hourglass_top'} tone={l.is_completed ? 'done' : 'muted'} />
                            }
                            title={l.title}
                            meta={l.module_title || undefined}
                            to={`${base}/students/${userId}/lessons/${l.lesson_id}`}
                            last={i === m.lessons.length - 1}
                            below={
                              notes.length || action ? (
                                <div data-progress-lesson-actions className="space-y-2 pb-3">
                                  {notes.length > 0 && (
                                    <p className="-mt-1 pl-[46px] text-[11.5px] text-on-surface-variant">{notes.join(' · ')}</p>
                                  )}
                                  {action}
                                </div>
                              ) : undefined
                            }
                          />
                        );
                      })}
                    </MBlock>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    );
  }

  if (lessons.isLoading) {
    return (
      <div className="space-y-4">
        {back}
        <Card variant="subtle">{t('common.loading')}</Card>
      </div>
    );
  }

  if (lessons.isError) {
    return (
      <div className="space-y-4">
        {back}
        <Card variant="subtle" className="text-center text-error">{humanizeError(lessons.error)}</Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {back}

      <Card className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Avatar name={name} src={student.data?.avatar_url} size="lg" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-display text-2xl font-bold tracking-display text-on-surface">{name}</h1>
          {student.data?.email && <p className="truncate text-sm text-on-surface-variant">{student.data.email}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Pill variant={passed > 0 ? 'success' : 'neutral'}>
              {t('studentProgress.passedOf', { passed, total })}
            </Pill>
            <span className="text-xs text-on-surface-variant tabular-nums">{percent}%</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-pill bg-surface-container-highest">
            <div className="h-full rounded-pill bg-primary transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>
      </Card>

      {total > 0 && (
        <Card variant="subtle" className="p-0">
          <label className="flex items-center gap-2 px-4 py-3">
            <span className="material-symbols-outlined text-[18px] text-on-surface-variant">search</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('studentProgress.searchPlaceholder')}
              className="w-full bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
            />
          </label>
        </Card>
      )}

      {total === 0 ? (
        <Card variant="subtle" className="space-y-1 py-8 text-center text-on-surface-variant">
          <p className="font-semibold text-on-surface">{t('studentProgress.emptyTitle')}</p>
          <p className="text-sm">{t('studentProgress.emptyDesc')}</p>
        </Card>
      ) : marathons.length === 0 ? (
        <Card variant="subtle" className="py-8 text-center text-on-surface-variant">
          {t('studentProgress.nothingFound')}
        </Card>
      ) : (
        marathons.map((m) => {
          // Где ученик стоит — по ПОЛНОМУ списку курса, а не по отфильтрованному поиском:
          // иначе поиск «урок 7» сделал бы седьмой урок «первым непройденным».
          const stuck = stuckLessonIds(
            data?.marathons.find((x) => x.marathon_id === m.marathon_id)?.lessons ?? m.lessons,
          );
          return (
          <Card key={m.marathon_id} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold text-on-surface">{m.title}</h2>
              <Pill variant="neutral">{t('studentProgress.passedOf', { passed: m.passed, total: m.total })}</Pill>
            </div>

            <ul className="divide-y divide-outline-variant/60">
              {m.lessons.map((l) => {
                const nextOpened = parseNextOpened(l.next_opened);
                const isStuck = !l.is_completed && stuck.has(l.lesson_id);
                const manualNote = manualCompletionNote(l);
                // Что под строкой — одно правило с телефоном (`lessonRowAction`).
                const act = lessonRowAction(m.grading_mode, l, isStuck);
                return (
                <li key={l.lesson_id} className="flex flex-wrap items-center gap-3 py-3">
                  <span
                    className={
                      l.is_completed
                        ? 'flex h-8 w-8 flex-none items-center justify-center rounded-full bg-success-container text-on-success-container'
                        : 'flex h-8 w-8 flex-none items-center justify-center rounded-full bg-surface-container-highest text-on-surface-variant'
                    }
                  >
                    {/* Песочные часы у непройденного — как в эталоне.
                        Оба знака обязаны быть в вырезанном шрифте значков,
                        иначе на экране появится их имя заглавными буквами. */}
                    <span className="material-symbols-outlined text-[18px]">
                      {l.is_completed ? 'check' : 'hourglass_top'}
                    </span>
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-on-surface">{l.title}</p>
                    {l.module_title && (
                      <p className="truncate text-xs text-on-surface-variant">{l.module_title}</p>
                    )}
                  </div>

                  {/* ⚠️ Поле оценки — только в режиме «проверка куратором».
                      В режиме auto балл ставит система, и куратор его не
                      правит: иначе рейтинг стал бы смесью двух правил.
                      У мини-курсов сервер всегда отдаёт auto — режим на них
                      не действует, и поля здесь не будет. */}
                  {/* Сервер ещё без фидбека (поля `curator_grade` нет) — прежнее
                      поле оценки, как было до D11. */}
                  {act === 'grade' && userId && (
                    <LessonGradeField
                      studentId={userId}
                      lessonId={l.lesson_id}
                      grade={l.grade}
                      isCompleted={l.is_completed}
                    />
                  )}

                  <Link
                    to={`${base}/students/${userId}/lessons/${l.lesson_id}`}
                    className="inline-flex flex-none items-center gap-1 rounded-pill bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition hover:opacity-90"
                  >
                    {t('studentProgress.openLesson')}
                    <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                  </Link>

                  {/* Ручные действия сотрудника видны у урока (договор lesson-force-complete.md §4):
                      иначе засчитанный вручную урок не отличить от пройденного учеником. Отдельной
                      строкой во всю ширину: в колонке названия на 390 отметка «кто · когда» шла
                      по слову в строку рядом с кнопкой «Открыть урок». */}
                  {(l.completed_manually || nextOpened) && (
                    <div className="flex basis-full flex-wrap items-center gap-x-2 gap-y-1 pl-11 text-xs text-on-surface-variant">
                      {l.completed_manually && <Pill variant="neutral">{t('studentProgress.completedManually')}</Pill>}
                      {/* «Ответов 0, урок не открывал» — чтобы пустой разбор не читался как пропавшие
                          ответы (26.09.2026, вариант В). Старый сервер полей не шлёт — пометки нет. */}
                      {manualNote && <Pill variant="warning">{t(MANUAL_NOTE_KEYS[manualNote])}</Pill>}
                      {nextOpened && (
                        <span>
                          {t('studentProgress.nextOpenedBy', {
                            name: nextOpened.name ?? t('studentProgress.unknownCurator'),
                            date: new Date(nextOpened.at).toLocaleString(i18n.language),
                          })}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ⚠️ Оценка и фидбек одной формой (D11) — под строкой урока, во
                      всю ширину: многострочному тексту в строке тесно. */}
                  {act === 'feedback' && userId && (
                    <div className="basis-full">
                      <LessonFeedbackEditor
                        studentId={userId}
                        lessonId={l.lesson_id}
                        lessonTitle={l.title}
                        curatorGrade={parseCuratorGrade(l.curator_grade)}
                        isCompleted={l.is_completed}
                        canComplete={isStuck}
                      />
                    </div>
                  )}

                  {/* «Открыть следующий» — курс «оценивает система» и мини-курс (сервер отдаёт им
                      auto). Строго auto, а не «не куратор»: старый сервер режима не присылает, и
                      кнопка без ручки отказала бы 404. Только там, где ученик стоит (§3). */}
                  {act === 'openNext' && userId && (
                    <div className="basis-full">
                      <OpenNextLessonButton studentId={userId} lessonId={l.lesson_id} lessonTitle={l.title} />
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          </Card>
          );
        })
      )}
    </div>
  );
}
